import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapContainer, GeoJSON, useMap, Pane, CircleMarker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Tooltip as AntTooltip, Button, message, Input, List, Modal } from 'antd';
import {
  PlayCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
  CloseOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { ExchangeIcon } from '../../constants/icons';
import { useLoading } from '../../contexts/LoadingContext';
import { getClientType } from '../../services/clientConfig';
import { fetchClientJSON } from '../../services/clientDataService';
import { PRIMARY_COLORS, SEMANTIC_COLORS } from '../../constants/colors';
import * as turf from '@turf/turf';
import {
  TraffixTileLayer, ThemeToggleButton, FitBoundsOnce, MapRefCapture, useSyncedMaps,
  ModuleSlider, ZoomButtons, CurrentLocationButton,
} from '../../common/map';
import '../../styles/mappa.css';
import './AssignxPage.css';
import {
  getFirstName, flowToColor, flowToWeight, estimateCapacityVph, INTERVENTION_COLORS,
  computeScenarioKPIs, simulateScenarioFlows,
} from './model/metrics';
import {
  emptyIntervention, closedEdgeUids, toApiScenario,
  countInterventions, isActiveIntervention, validateNewRoad, fromApiInterventions,
  directionalClosureState, applyDirectionalClosure, booleanFlag, isOneWayEdge,
  replaceClosedEdgeUids, scenarioDefinitionFingerprint,
} from './model/interventions';
import {
  MAP_SELECTED_COLOR, MAP_MUTED_COLOR,
} from '../../constants/scoreScale';
import * as assignxApi from './APIs/assignxApi';
import {
  extractGraphNodes,
  materializeNewRoadFeatures,
  mergeScenarioResult,
  normalizePersistedRoad,
} from './model/network';
import {
  canUseLocalFallback,
  isEndpointUnavailable,
  deleteLocalScenario,
  listLocalScenarios,
  saveLocalScenario,
  updateLocalScenario,
} from './services/assignxLocalStore';
import LinkDetailPanel from './components/LinkDetailPanel';
import { InterventionsDrawer, NewRoadPanel } from './components/AssignxDrawers';

const BASE_FLOWS_FILE = 'assignx/assignx_network.geojson';
const DELTA_INCREASE_COLOR = '#ff4d4f';
const DELTA_DECREASE_COLOR = '#52c41a';

const DEFAULT_CENTER = [44.309405, 9.352045];
const DEFAULT_ZOOM = 13;
const VIEWPORT_BUFFER = 0.15;
const INITIAL_REQUEST_TIMEOUT = 15_000;
// The network catalog is built from the client graph, so it needs a far larger
// budget than the static base-flows file.
const NETWORK_REQUEST_TIMEOUT = 120_000;
const EMPTY_SET = new Set();

function assignxErrorMessage(error, fallback) {
  const data = error?.data || error;
  const errors = [
    ...(data?.validation?.errors || []),
    ...(data?.errors || []),
    ...(data?.left?.validation?.errors || []),
    ...(data?.right?.validation?.errors || []),
  ];
  const details = errors.map((item) => item?.message || item?.code).filter(Boolean);
  return details.join(', ') || data?.error || error?.message || fallback;
}

function buildLocalScenarioGeoJson(baseGeoJson, graphNodes, side, edgeMeta = {}) {
  if (!baseGeoJson) return null;
  const roadFeatures = materializeNewRoadFeatures(side.newRoads || [], graphNodes);
  const features = [...(baseGeoJson.features || []), ...roadFeatures];
  const simulation = simulateScenarioFlows(
    baseGeoJson.features || [],
    side.interventions || {},
    side.newRoads || [],
    edgeMeta,
  );
  return {
    ...baseGeoJson,
    features: features.map((feature) => {
      const uid = feature.properties?.edge_uid;
      if (!uid) return feature;
      const edge = simulation.edges.find((candidate) => candidate.uid === uid);
      const baseFlow = Number(feature.properties?.flow || 0);
      const nextFlow = simulation.flow[uid] ?? baseFlow;
      return {
        ...feature,
        properties: {
          ...feature.properties,
          flow: nextFlow,
          delta_flow: nextFlow - baseFlow,
          capacity_vph: edge ? simulation.effectiveCapacity(edge) : feature.properties?.capacity_vph,
        },
      };
    }),
  };
}

function withScenarioRoads(baseGeoJson, graphNodes, roads) {
  if (!baseGeoJson) return null;
  return {
    ...baseGeoJson,
    features: [
      ...(baseGeoJson.features || []),
      ...materializeNewRoadFeatures(roads || [], graphNodes),
    ],
  };
}

/* ── Helpers ── */
function withTimeout(promise, timeoutMs = INITIAL_REQUEST_TIMEOUT) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error('AssignX request timed out')), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timeoutId));
}

function simplifyCoords(coords) {
  if (typeof coords[0] === 'number') {
    return coords.map(c => Math.round(c * 1e6) / 1e6);
  }
  return coords.map(simplifyCoords);
}

function simplifyGeoJSON(geojson) {
  if (!geojson?.features) return geojson;
  return {
    ...geojson,
    features: geojson.features.map(f => ({
      ...f,
      geometry: {
        ...f.geometry,
        coordinates: simplifyCoords(f.geometry.coordinates),
      },
    })),
  };
}

function edgeMetaFromGeoJSON(geojson) {
  const meta = {};
  const byDirection = new Map();
  (geojson?.features || []).forEach((feature) => {
    const props = feature.properties || {};
    const uid = props.edge_uid;
    if (!uid) return;
    const parts = String(uid).split('_');
    const u = props.u ?? parts[0];
    const v = props.v ?? parts[1];
    meta[uid] = {
      ...props,
      edge_uid: uid,
      u,
      v,
      capacity_vph: Number(props.capacity_vph) || estimateCapacityVph(props.highway, props.lanes),
      capacity_estimated: props.capacity_vph == null,
      oneway: props.oneway == null ? null : booleanFlag(props.oneway),
    };
    if (u != null && v != null) {
      const key = `${u}|${v}`;
      if (!byDirection.has(key)) byDirection.set(key, []);
      byDirection.get(key).push(uid);
    }
  });
  Object.values(meta).forEach((edge) => {
    if (edge.u == null || edge.v == null) return;
    const candidates = byDirection.get(`${edge.v}|${edge.u}`) || [];
    const key = String(edge.edge_uid).split('_').at(-1);
    const exactUid = `${edge.v}_${edge.u}_${key}`;
    edge.opposite_uid = candidates.find((uid) => uid === exactUid)
      || candidates.find((uid) => {
        const candidate = meta[uid] || {};
        return candidate.osmid != null
          && edge.osmid != null
          && String(candidate.osmid) === String(edge.osmid);
      })
      || candidates.find((uid) => {
        const candidate = meta[uid] || {};
        return candidate.name === edge.name && candidate.highway === edge.highway;
      })
      || candidates.find((uid) => uid === edge.opposite_uid)
      || candidates[0]
      || null;
    edge.oneway = edge.oneway == null ? !edge.opposite_uid : edge.oneway;
  });
  return meta;
}

function featureIntersectsBounds(feature, bounds) {
  const coords = feature.geometry?.coordinates;
  if (!coords) return false;
  const type = feature.geometry.type;
  let points;
  if (type === 'Point') points = [coords];
  else if (type === 'LineString') points = coords;
  else if (type === 'MultiLineString' || type === 'Polygon') points = coords.flat();
  else if (type === 'MultiPolygon') points = coords.flat(2);
  else return true;

  for (const p of points) {
    if (p[1] >= bounds.south && p[1] <= bounds.north &&
      p[0] >= bounds.west && p[0] <= bounds.east) {
      return true;
    }
  }
  return false;
}

/* ── Components ── */

/** Safely run a Leaflet map operation (avoids _leaflet_pos crashes on torn-down maps). */
function withLiveMap(map, fn) {
  if (!map) return false;
  try {
    const container = map.getContainer?.();
    if (!container?.parentNode || !map._mapPane) return false;
    if (typeof map.stop === 'function') map.stop();
    fn(map);
    return true;
  } catch (e) {
    console.warn('[Assignx Map] Skipped map operation:', e?.message || e);
    return false;
  }
}

function isLiveMap(map) {
  try {
    return !!(map && map.getContainer?.()?.parentNode && map._mapPane);
  } catch {
    return false;
  }
}

function ViewportFilteredGeoJSON({ data, style, onEachFeature, interactive, geoJsonRef, revision = 0 }) {
  const map = useMap();
  const [visibleData, setVisibleData] = useState(null);
  const filterTimeoutRef = useRef(null);

  const filterFeatures = useCallback(() => {
    if (!data?.features?.length || !isLiveMap(map)) return;
    try {
      const b = map.getBounds();
      const latBuffer = (b.getNorth() - b.getSouth()) * VIEWPORT_BUFFER;
      const lngBuffer = (b.getEast() - b.getWest()) * VIEWPORT_BUFFER;
      const bounds = {
        south: b.getSouth() - latBuffer,
        north: b.getNorth() + latBuffer,
        west: b.getWest() - lngBuffer,
        east: b.getEast() + lngBuffer,
      };
      const filtered = data.features.filter(f => featureIntersectsBounds(f, bounds));
      setVisibleData({ type: 'FeatureCollection', features: filtered });
    } catch (_) { /* map torn down mid-move */ }
  }, [data, map]);

  useEffect(() => {
    filterFeatures();
    const onMoveEnd = () => {
      if (filterTimeoutRef.current) cancelAnimationFrame(filterTimeoutRef.current);
      filterTimeoutRef.current = requestAnimationFrame(filterFeatures);
    };
    map.on('moveend', onMoveEnd);
    map.on('zoomend', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
      map.off('zoomend', onMoveEnd);
      if (filterTimeoutRef.current) cancelAnimationFrame(filterTimeoutRef.current);
    };
  }, [map, filterFeatures]);

  if (!visibleData?.features?.length) return null;

  return (
    <GeoJSON
      key={`${revision}_${visibleData.features.length}_${visibleData.features[0]?.properties?.edge_uid}_${visibleData.features[0]?.properties?.flow}`}
      data={visibleData}
      style={style}
      onEachFeature={onEachFeature}
      interactive={interactive}
      ref={geoJsonRef}
    />
  );
}
function ZoomListener({ onZoomChange }) {
  const map = useMap();
  useEffect(() => {
    if (!isLiveMap(map)) return undefined;
    onZoomChange(map.getZoom());
    const handler = () => {
      if (!isLiveMap(map)) return;
      try { onZoomChange(map.getZoom()); } catch (_) { /* ignore */ }
    };
    map.on('zoomend', handler);
    return () => map.off('zoomend', handler);
  }, [map, onZoomChange]);
  return null;
}

function MapClickDismiss({ onMapClick }) {
  const map = useMap();
  useEffect(() => {
    const handler = () => { if (onMapClick) onMapClick(); };
    map.on('click', handler);
    return () => map.off('click', handler);
  }, [map, onMapClick]);
  return null;
}

function AssignxSideCard({
  baseFlows,
  closedEdges,
  setClosedEdges,
  highlightEdges,
  onSelectSearchResult,
  onOpenStreet,
  streetsByName,
  setHoveredUids,
  edgeMeta,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const searchResults = useMemo(() => {
    if (!searchTerm) return [];
    const term = searchTerm.toLowerCase();
    return Array.from(streetsByName.keys())
      .filter(name => name.toString().toLowerCase().includes(term))
      .sort();
  }, [searchTerm, streetsByName]);

  const selectedSegments = useMemo(() => {
    const groups = new Map();
    if (!baseFlows?.features) return [];

    baseFlows.features.forEach(f => {
      const uid = f.properties.edge_uid;
      if (!closedEdges.has(uid)) return;

      let name = getFirstName(f.properties.name);
      if (!name || name === 'Strada') {
        name = 'Strada senza nome';
      }

      const groupKey = name.trim();

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          name: groupKey,
          uids: [],
          features: []
        });
      }
      const g = groups.get(groupKey);
      g.uids.push(uid);
      g.features.push(f);
    });

    return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [closedEdges, baseFlows]);

  const handleSelectStreet = (name) => {
    if (onOpenStreet) onOpenStreet(name);
    else if (onSelectSearchResult) onSelectSearchResult(name);
    setSearchOpen(false);
  };

  const directionPair = (features, preferClosed = false) => {
    const primary = (preferClosed
      ? features.find((feature) => closedEdges.has(feature.properties?.edge_uid))
      : null) || features[0];
    const forwardUid = primary?.properties?.edge_uid || null;
    const matchedOppositeUid = forwardUid
      ? (edgeMeta[forwardUid]?.opposite_uid || primary?.properties?.opposite_uid || null)
      : null;
    const twoWay = !!forwardUid && !isOneWayEdge(edgeMeta[forwardUid], primary?.properties);
    const oppositeUid = twoWay ? matchedOppositeUid : null;
    return { forwardUid, oppositeUid, twoWay };
  };

  return (
    <div className={`assignx-side-card-container ${searchOpen ? 'open' : ''}`}>
      <div className="assignx-side-card white-theme">
        <div className="assignx-side-header">
          <button
            type="button"
            className={`sidebar-button assignx-search-toggle ${searchTerm ? 'active' : ''}`}
            aria-label="Cerca strada"
            onClick={() => setSearchOpen((value) => !value)}
          >
            <SearchOutlined />
          </button>
          {searchOpen && (
            <Input
              autoFocus
              placeholder="Cerca strada..."
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                if (!event.target.value) onSelectSearchResult?.(null);
              }}
              allowClear
              className="assignx-search-input-v2"
            />
          )}
        </div>
        <div className={`assignx-side-body ${searchOpen && searchTerm ? 'has-results' : ''}`}>
          {searchTerm ? (
            <List
              size="small"
              dataSource={searchResults}
              renderItem={name => {
                const features = streetsByName.get(name) || [];
                const streetUids = features.map(f => f.properties.edge_uid);
                const isSelected = streetUids.some(uid => closedEdges.has(uid));
                const { forwardUid, oppositeUid, twoWay } = directionPair(features);
                const pairUids = [forwardUid, oppositeUid].filter(Boolean);

                const toggleStreetDir = (isReverse) => {
                  const targets = [isReverse ? oppositeUid : forwardUid].filter(Boolean);

                  setClosedEdges(prev => {
                    const next = new Set(prev);
                    const anyClosed = targets.some(t => next.has(t));
                    if (anyClosed) targets.forEach(t => next.delete(t));
                    else targets.forEach(t => next.add(t));
                    return next;
                  });
                };

                const isDir1Closed = !!forwardUid && closedEdges.has(forwardUid);
                const isDir2Closed = !!oppositeUid && closedEdges.has(oppositeUid);

                return (
                  <div
                    className={`assignx-street-item-v2${isSelected ? ' selected' : ''}`}
                    onClick={() => handleSelectStreet(name)}
                    onMouseEnter={() => {
                      highlightEdges && highlightEdges(pairUids, true);
                      if (highlightEdges && onSelectSearchResult) onSelectSearchResult(name);
                      if (setHoveredUids) setHoveredUids(pairUids);
                    }}
                    onMouseLeave={() => {
                      highlightEdges && highlightEdges(pairUids, false);
                      if (setHoveredUids) setHoveredUids(null);
                    }}
                  >
                    <div className="assignx-street-name-v2">{name}</div>
                    <div className="assignx-segment-ways">
                      <div
                        className={`assignx-way-line-v2 ${isDir1Closed ? 'closed' : 'open'}`}
                        title="Direzione 1"
                        onClick={(e) => { e.stopPropagation(); toggleStreetDir(false); }}
                      />
                      {twoWay && <div
                        className={`assignx-way-line-v2 ${isDir2Closed ? 'closed' : 'open'}`}
                        title="Direzione 2"
                        onClick={(e) => { e.stopPropagation(); toggleStreetDir(true); }}
                      />}
                    </div>
                  </div>
                );
              }}
            />
          ) : (
            selectedSegments.length === 0 ? (
              <p className="assignx-empty-text-v2">Nessuna strada selezionata.</p>
            ) : (
              <div className="assignx-segment-list">
                {selectedSegments.map(group => {
                  const name = group.name;
                  const allFeatures = streetsByName.get(name) || group.features;
                  const streetUids = allFeatures.map(f => f.properties.edge_uid);
                  const { forwardUid, oppositeUid, twoWay } = directionPair(allFeatures, true);
                  const pairUids = [forwardUid, oppositeUid].filter(Boolean);

                  const toggleStreetDir = (isReverse) => {
                    const targets = [isReverse ? oppositeUid : forwardUid].filter(Boolean);

                    setClosedEdges(prev => {
                      const next = new Set(prev);
                      const anyClosed = targets.some(t => next.has(t));
                      if (anyClosed) targets.forEach(t => next.delete(t));
                      else targets.forEach(t => next.add(t));
                      return next;
                    });
                  };

                  const isDir1Closed = !!forwardUid && closedEdges.has(forwardUid);
                  const isDir2Closed = !!oppositeUid && closedEdges.has(oppositeUid);

                  return (
                    <div key={group.name} className="assignx-segment-item"
                      onMouseEnter={() => {
                        highlightEdges && highlightEdges(pairUids, true);
                        if (highlightEdges && onSelectSearchResult) onSelectSearchResult(name);
                        if (setHoveredUids) setHoveredUids(pairUids);
                      }}
                      onMouseLeave={() => {
                        highlightEdges && highlightEdges(pairUids, false);
                        if (setHoveredUids) setHoveredUids(null);
                      }}
                    >
                      <div className="assignx-segment-header">
                        <span className="assignx-segment-name">{name}</span>
                        <Button
                          type="text" danger size="small"
                          icon={<DeleteOutlined />}
                          className="assignx-segment-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setClosedEdges(prev => {
                              const next = new Set(prev);
                              streetUids.forEach(u => next.delete(u));
                              return next;
                            });
                          }}
                        />
                      </div>
                      <div className="assignx-segment-ways">
                        <div
                          className={`assignx-way-line-v2 ${isDir1Closed ? 'closed' : 'open'}`}
                          onClick={(e) => { e.stopPropagation(); toggleStreetDir(false); }}
                        />
                        {twoWay && <div
                          className={`assignx-way-line-v2 ${isDir2Closed ? 'closed' : 'open'}`}
                          onClick={(e) => { e.stopPropagation(); toggleStreetDir(true); }}
                        />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

const AssignxNodeLayer = React.memo(function AssignxNodeLayer({
  nodes,
  visible,
  selectable,
  originId,
  destinationId,
  onPickNode,
}) {
  const renderer = useMemo(() => L.svg({ padding: 0.25 }), []);
  if (!visible) return null;

  return nodes.map((node) => {
    const isOrigin = originId === node.id;
    const isDestination = destinationId === node.id;
    const safeId = String(node.id).replace(/[^a-zA-Z0-9_-]/g, '-');
    return (
      <CircleMarker
        key={node.id}
        className={`assignx-node-marker assignx-node-${safeId}`}
        center={[node.lat, node.lon]}
        radius={isOrigin || isDestination ? 8 : 5}
        pathOptions={{
          color: '#fff',
          weight: isOrigin || isDestination ? 2 : 1,
          fillColor: isOrigin
            ? SEMANTIC_COLORS.success
            : isDestination
              ? SEMANTIC_COLORS.warning
              : PRIMARY_COLORS.brandPurple,
          fillOpacity: 0.9,
        }}
        renderer={renderer}
        eventHandlers={selectable && onPickNode ? { click: () => onPickNode(node) } : undefined}
      />
    );
  });
});

/* ════════════════ AssignxMapView (Individual Map) ════════════════ */
function AssignxMapView({
  id,
  mapRef,
  mapTheme,
  baseFlows,
  resultFlows,
  closedEdges,
  interventions = {},
  edgeMeta = {},
  newRoads = [],
  onMapReady,
  onMapBackgroundClick,
  onSelectSearchResult,
  streetsByName,
  externalLayerMapRef,
  hoveredUids,
  focusedUids,
  baseMax,
  onOpenLink,
  newRoadMode,
  graphNodes = [],
  newRoadDraft,
  onPickNode,
  autoFit = true,
  scenarioSelect = null,
}) {
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const internalLayerMapRef = useRef(new Map());
  const layerMapRef = externalLayerMapRef || internalLayerMapRef;
  const geoJsonRef = useRef(null);
  const fittedRef = useRef(!autoFit);
  const resultRevisionRef = useRef(0);
  const previousResultRef = useRef(null);
  if (previousResultRef.current !== resultFlows) {
    previousResultRef.current = resultFlows;
    resultRevisionRef.current += 1;
  }

  const isBase = !resultFlows;
  const resultMax = useMemo(() => {
    if (!resultFlows?.features?.length) return 1;
    return resultFlows.features.reduce((max, feature) => Math.max(max, feature.properties?.flow ?? 0), 1);
  }, [resultFlows]);
  const clickedLayerRef = useRef(false);

  const interventionStyle = useCallback((feature) => {
    const uid = feature.properties?.edge_uid;
    const a = interventions[uid] || {};
    const max = resultFlows ? resultMax : baseMax;
    const hasSelection = Boolean(focusedUids?.size);
    const isFocused = hasSelection && focusedUids.has(uid);
    if (hasSelection && !isFocused) {
      return {
        color: MAP_MUTED_COLOR, weight: 2, opacity: 0.55, dashArray: null,
        lineJoin: 'round', lineCap: 'round', interactive: true,
      };
    }
    const focusOpacity = 1;
    const focusedWeight = isFocused ? 2 : 0;
    if (a.both || a.forward || closedEdges.has(uid)) {
      return { color: INTERVENTION_COLORS.closed, weight: 5 + focusedWeight, opacity: focusOpacity, dashArray: '8 4', interactive: true };
    }
    if (a.reverse) {
      return { color: INTERVENTION_COLORS.reverse, weight: 5 + focusedWeight, opacity: focusOpacity, dashArray: '1 6', interactive: true };
    }
    if (isActiveIntervention(a, edgeMeta[uid]?.capacity_vph) && (a.newCap != null || Number(a.factor) !== 1)) {
      return { color: INTERVENTION_COLORS.capacity, weight: 5 + focusedWeight, opacity: focusOpacity, interactive: true };
    }
    if (feature.properties?.is_new) {
      return { color: INTERVENTION_COLORS.news, weight: 5 + focusedWeight, opacity: focusOpacity, interactive: true };
    }
    const delta = Number(feature.properties?.delta_flow);
    if (resultFlows && Number.isFinite(delta) && Math.abs(delta) >= 1) {
      return {
        color: isFocused ? MAP_SELECTED_COLOR : (delta > 0 ? DELTA_INCREASE_COLOR : DELTA_DECREASE_COLOR),
        weight: 6 + focusedWeight,
        opacity: focusOpacity,
        interactive: true,
      };
    }
    return {
      color: isFocused ? MAP_SELECTED_COLOR : flowToColor(feature.properties?.flow ?? 0, max),
      weight: flowToWeight(feature.properties?.flow ?? 0, max) + focusedWeight,
      opacity: isFocused ? 1 : 0.85, dashArray: null,
      lineJoin: 'round', lineCap: 'round', interactive: true,
    };
  }, [interventions, closedEdges, edgeMeta, resultFlows, resultMax, baseMax, focusedUids]);

  const baseStyle = interventionStyle;
  const resultStyle = interventionStyle;

  useEffect(() => {
    if (!layerMapRef.current) return;
    layerMapRef.current.forEach((layer) => {
      if (layer.feature && typeof layer.setStyle === 'function') {
        const styleFn = isBase ? baseStyle : resultStyle;
        layer.setStyle(styleFn(layer.feature));
      }
    });
  }, [closedEdges, isBase, baseStyle, resultStyle, layerMapRef, focusedUids]);

  const getSplitFeature = useCallback((feature, direction) => {
    try {
      const scale = Math.pow(2, 17 - zoomLevel);
      const offsetVal = 0.00003 * Math.min(Math.max(scale, 0.1), 50);
      const offset = direction === 'forward' ? offsetVal : -offsetVal;
      const line = turf.helpers.feature(feature.geometry);
      const offsetLine = turf.lineOffset(line, offset, { units: 'degrees' });
      return {
        ...feature,
        geometry: offsetLine.geometry,
        id: `${feature.properties.edge_uid}_${direction}`
      };
    } catch (e) {
      return feature;
    }
  }, [zoomLevel]);

  const openClosurePopup = useCallback((edgeUid, layer, latlng) => {
    if (newRoadMode) return;
    if (!edgeUid) return;
    onOpenLink && onOpenLink(edgeUid, layer.feature, latlng);
  }, [onOpenLink, newRoadMode]);

  const onFeature = useCallback((feature, layer) => {
    const { edge_uid } = feature.properties ?? {};
    layerMapRef.current.set(edge_uid, layer);
    if (layer.setStyle) layer.setStyle({ interactive: true });

    const updateTooltip = () => {
      const name = getFirstName(feature.properties?.name);
      const flowVal = (feature.properties?.flow ?? 0).toFixed(2);
      const timeVal = (feature.properties?.travel_time ?? 0).toFixed(2);
      const delta = Number(feature.properties?.delta_flow);
      const deltaLine = Number.isFinite(delta) && Math.abs(delta) >= 1
        ? `<br/><span>Variazione: ${delta > 0 ? '+' : ''}${delta.toFixed(0)}</span>`
        : '';
      const tip = `<div class="assignx-tooltip">
          <strong>${name}</strong><br/>
          <span>Flusso: ${flowVal}</span><br/>
          <span>Tempo: ${timeVal}s</span>
          ${deltaLine}
          ${`<br/><em class="tip-action">Clicca per configurare chiusura, senso e capacità</em>`}
         </div>`;
      if (layer.getTooltip()) layer.setTooltipContent(tip);
      else layer.bindTooltip(tip, { sticky: true, direction: 'top', className: 'assignx-tooltip-container', interactive: true });
    };
    updateTooltip();
    layer.on('mouseover', updateTooltip);

    layer.on('click', (e) => {
      if (newRoadMode) return;
      clickedLayerRef.current = true;
      setTimeout(() => { clickedLayerRef.current = false; }, 50);
      openClosurePopup(edge_uid, layer, e.latlng);
    });
  }, [openClosurePopup, layerMapRef, newRoadMode]);

  return (
    <div className="assignx-map-view" id={id}>
      {scenarioSelect && (
        <label className="assignx-scenario-pill">
          <select
            aria-label={scenarioSelect.label}
            value={scenarioSelect.value}
            disabled={scenarioSelect.disabled}
            onChange={(event) => scenarioSelect.onChange(event.target.value)}
          >
            {scenarioSelect.options.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
        </label>
      )}
      <MapContainer
        center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM}
        maxZoom={22} zoomControl={false} className="assignx-map"
        preferCanvas={true}
      >
        <Pane name="split-roads-pane" style={{ zIndex: 1000, pointerEvents: 'none' }} />
        <MapRefCapture mapRef={mapRef} onMapReady={onMapReady} />
        <ZoomListener onZoomChange={setZoomLevel} />
        <MapClickDismiss onMapClick={() => {
          if (!clickedLayerRef.current && onMapBackgroundClick) onMapBackgroundClick();
        }} />
        <TraffixTileLayer theme={mapTheme} />

        {isBase && baseFlows && (
          <>
            <ViewportFilteredGeoJSON
              data={baseFlows} style={baseStyle}
              onEachFeature={onFeature}
              interactive={true} geoJsonRef={geoJsonRef}
            />
            <FitBoundsOnce data={baseFlows} fittedRef={fittedRef} />
          </>
        )}

        {hoveredUids && baseFlows && (() => {
          const processed = new Set();
          const pairs = [];
          const features = baseFlows.features || [];
          for (const uid of hoveredUids) {
            if (processed.has(uid)) continue;
            processed.add(uid);
            const forwardFeat = features.find((feature) => feature.properties?.edge_uid === uid);
            if (!forwardFeat) continue;
            const explicitOpposite = edgeMeta[uid]?.opposite_uid || forwardFeat.properties?.opposite_uid;
            const reverseFeat = explicitOpposite
              ? features.find((feature) => feature.properties?.edge_uid === explicitOpposite)
              : features.find((feature) => {
                const properties = feature.properties || {};
                return hoveredUids.includes(properties.edge_uid)
                  && String(properties.u) === String(forwardFeat.properties?.v)
                  && String(properties.v) === String(forwardFeat.properties?.u);
              });
            const reverseUid = reverseFeat?.properties?.edge_uid;
            if (reverseUid) processed.add(reverseUid);
            pairs.push({
              forwardFeat,
              forwardUid: uid,
              reverseFeat,
              reverseUid,
              twoWay: !isOneWayEdge(edgeMeta[uid], forwardFeat.properties),
            });
          }
          return pairs.map(({ forwardFeat, forwardUid, reverseUid, twoWay }) => {
            const forward = getSplitFeature(forwardFeat, 'forward');
            // Use one geometry for both offsets: the opposite edge is usually
            // digitized in reverse, which would otherwise put both offsets on
            // the same side of the centerline.
            const backward = getSplitFeature(forwardFeat, 'backward');
            return (
              <React.Fragment key={`hover-${forwardUid}`}>
                <GeoJSON data={forward} pathOptions={{ className: 'assignx-split-direction assignx-split-forward' }} style={{ color: closedEdges.has(forwardUid) ? '#ff4d4f' : '#52c41a', weight: 8, opacity: 1 }} pane="split-roads-pane" interactive={false} />
                {twoWay && <GeoJSON data={backward} pathOptions={{ className: 'assignx-split-direction assignx-split-backward' }} style={{ color: reverseUid && closedEdges.has(reverseUid) ? '#ff4d4f' : '#52c41a', weight: 8, opacity: 1 }} pane="split-roads-pane" interactive={false} />}
              </React.Fragment>
            );
          });
        })()}

        {!isBase && resultFlows && (
          <>
            <ViewportFilteredGeoJSON
              data={resultFlows}
              style={resultStyle}
              onEachFeature={onFeature}
              interactive={true}
              geoJsonRef={geoJsonRef}
              revision={resultRevisionRef.current}
            />
            <FitBoundsOnce data={resultFlows} fittedRef={fittedRef} />
          </>
        )}

        <div className="assignx-map-controls">
          <CurrentLocationButton />
          <ZoomButtons />
        </div>

        <div className="assignx-legend">
          <div className="assignx-legend-title">Flusso Traffico</div>
          <div className="legend-gradient-container">
            <div className="legend-gradient-bar gradient-assignx-flow"></div>
            <div className="legend-labels">
              <span className="legend-label">Basso</span>
              <span className="legend-label">Alto</span>
            </div>
          </div>
          <div className="assignx-legend-mini">
            {!isBase && (
              <>
                <div><span className="dot increase" />Flusso in aumento</div>
                <div><span className="dot decrease" />Flusso in diminuzione</div>
              </>
            )}
            <div><span className="dot closed" />Strada chiusa</div>
            <div><span className="dot amber" />Capacità modificata</div>
            <div><span className="dot purple" />Senso invertito</div>
            <div><span className="dot new" />Nuova strada</div>
          </div>
        </div>
        {newRoadMode && newRoadDraft?.origin && newRoadDraft?.destination && (() => {
          const origin = graphNodes.find((node) => node.id === newRoadDraft.origin);
          const destination = graphNodes.find((node) => node.id === newRoadDraft.destination);
          return origin && destination ? <Polyline positions={[[origin.lat, origin.lon], [destination.lat, destination.lon]]} pathOptions={{ color: INTERVENTION_COLORS.news, weight: 5, dashArray: '8 7', opacity: .9 }} /> : null;
        })()}
        <AssignxNodeLayer
          nodes={graphNodes}
          visible={newRoadMode}
          selectable={newRoadMode}
          originId={newRoadDraft?.origin}
          destinationId={newRoadDraft?.destination}
          onPickNode={onPickNode}
        />
      </MapContainer>
    </div>
  );
}

/* ════════════════ AssignxPage (Controller) ════════════════ */
export default function AssignxPage() {
  const { showLoader, hideLoader } = useLoading();
  const [mapTheme, setMapTheme] = useState('dark');
  const [baseFlows, setBaseFlows] = useState(null);
  const [graphNodes, setGraphNodes] = useState([]);
  const [loadingNet, setLoadingNet] = useState(true);
  const [compareMode, setCompareMode] = useState(false);
  const [messageApi, ctx] = message.useMessage();
  const loadMessageApiRef = useRef(messageApi);
  useEffect(() => {
    loadMessageApiRef.current = messageApi;
  }, [messageApi]);
  const [interventions, setInterventions] = useState({});
  const [newRoads, setNewRoads] = useState([]);
  const [edgeMeta, setEdgeMeta] = useState({});
  const [networkKpis, setNetworkKpis] = useState(null);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [scName, setScName] = useState('');
  const [savePromptPurpose, setSavePromptPurpose] = useState(null);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [savedScenarioFingerprint, setSavedScenarioFingerprint] = useState(null);
  const [focusedStreetName, setFocusedStreetName] = useState(null);
  const [linkSel, setLinkSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [newRoadMode, setNewRoadMode] = useState(false);
  const [newRoadDraft, setNewRoadDraft] = useState({ origin: null, destination: null, highway: 'residential', lanes: 1, speed: 30, capacity: 400, capacityAuto: true, factor: 1, twoWay: false, name: '' });
  const [showNewRoadForm, setShowNewRoadForm] = useState(false);
  const [compareLeft, setCompareLeft] = useState('base');
  const [compareRight, setCompareRight] = useState('current');
  const [kpiDelta, setKpiDelta] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [compareRunning, setCompareRunning] = useState(false);
  const comparisonRequestRef = useRef(0);
  const [activeAction, setActiveAction] = useState(null);
  const actionLockRef = useRef(false);
  const apiCapabilitiesRef = useRef({
    execution: null,
    scenarios: null,
    scenarioWrites: null,
    roads: null,
    roadWrites: null,
  });

  // `deferToRequest` leaves the loading screen to the HTTP layer, which reports
  // real progress for the call. Opening a second spinner-only operation here
  // would hide that percentage.
  const startAction = useCallback((label, { deferToRequest = false } = {}) => {
    if (actionLockRef.current) return false;
    actionLockRef.current = true;
    setActiveAction({ label, deferToRequest });
    return true;
  }, []);

  const finishAction = useCallback(() => {
    actionLockRef.current = false;
    setActiveAction(null);
  }, []);

  const actionBusy = loadingNet || !!activeAction;

  const newRoadFeatures = useMemo(() => materializeNewRoadFeatures(newRoads, graphNodes), [newRoads, graphNodes]);
  const liveFlows = useMemo(() => {
    if (!baseFlows) return null;
    return { ...baseFlows, features: [...baseFlows.features, ...newRoadFeatures] };
  }, [baseFlows, newRoadFeatures]);

  const baseMax = useMemo(() => {
    if (!liveFlows?.features) return 1;
    return liveFlows.features.reduce((a, f) => Math.max(a, f.properties?.flow ?? 0), 1);
  }, [liveFlows]);

  const closedEdgesA = useMemo(() => closedEdgeUids(interventions, edgeMeta), [interventions, edgeMeta]);
  const updateClosedEdges = useCallback((updater) => {
    setInterventions((current) => {
      const currentClosed = closedEdgeUids(current, edgeMeta);
      const updated = typeof updater === 'function' ? updater(currentClosed) : updater;
      return replaceClosedEdgeUids(current, updated);
    });
  }, [edgeMeta]);
  const [resultFlowsA, setResultFlowsA] = useState(null);
  const [runningA, setRunningA] = useState(false);
  const mapARef = useRef(null);
  const layerMapARef = useRef(new Map());
  const mapBRef = useRef(null);
  const layerMapBRef = useRef(new Map());

  useEffect(() => {
    if (loadingNet) {
      showLoader('Caricamento rete AssignX…', 1, { showPercentage: false });
    } else if (activeAction && !activeAction.deferToRequest) {
      showLoader(activeAction.label, 1, { showPercentage: false });
    } else hideLoader();
  }, [loadingNet, activeAction, showLoader, hideLoader]);

  const [hoveredUids, setHoveredUids] = useState(null);

  const edgeUidMap = useMemo(() => {
    const map = new Map();
    if (!liveFlows?.features) return map;
    for (const f of liveFlows.features) {
      const uid = f?.properties?.edge_uid;
      if (!uid) continue;
      const parts = uid.split('_');
      if (parts.length < 3) continue;
      const key = `${parts[0]}_${parts[1]}`;
      const arr = map.get(key) ?? [];
      arr.push(uid);
      map.set(key, arr);
    }
    return map;
  }, [liveFlows]);

  const featuresByUid = useMemo(() => {
    const m = new Map();
    (liveFlows?.features || []).forEach((f) => m.set(f.properties?.edge_uid, f));
    return m;
  }, [liveFlows]);

  const inferMeta = useCallback((uid, catalog) => {
    const fromApi = catalog?.[uid];
    if (fromApi) return fromApi;
    const parts = String(uid).split('_');
    const reverseKey = parts.length >= 2 ? `${parts[1]}_${parts[0]}` : '';
    const opps = reverseKey ? (edgeUidMap.get(reverseKey) || []) : [];
    const feat = featuresByUid.get(uid);
    return {
      oneway: opps.length === 0,
      opposite_uid: opps[0] || null,
      capacity_vph: feat?.properties?.capacity_vph || 600,
      highway: feat?.properties?.highway,
      speed_kph: feat?.properties?.speed_kph,
      u: parts[0],
      v: parts[1],
    };
  }, [edgeUidMap, featuresByUid]);

  useEffect(() => {
    const loadBase = async () => {
      const clientType = getClientType();
      const baseRequest = async () => {
        const data = await withTimeout(fetchClientJSON(clientType, BASE_FLOWS_FILE));
        return simplifyGeoJSON(data);
      };

      const [baseResult, networkResult, scenariosResult] = await Promise.allSettled([
        baseRequest(),
        assignxApi.fetchAssignxNetwork(clientType, { timeoutMs: NETWORK_REQUEST_TIMEOUT }),
        assignxApi.listScenarios(clientType, { timeoutMs: NETWORK_REQUEST_TIMEOUT }),
      ]);
      // Only a definitive "not deployed" answer may disable an endpoint. The
      // catalog request says nothing about /run, and a timeout says nothing at all.
      const markFromLoad = (key, result) => {
        if (result.status === 'fulfilled') apiCapabilitiesRef.current[key] = true;
        else if (isEndpointUnavailable(result.reason)) apiCapabilitiesRef.current[key] = false;
      };
      markFromLoad('roads', networkResult);
      markFromLoad('roadWrites', networkResult);
      markFromLoad('scenarios', scenariosResult);
      markFromLoad('scenarioWrites', scenariosResult);

      if (baseResult.status === 'fulfilled') {
        const simplified = baseResult.value;
        setGraphNodes(extractGraphNodes(simplified));
        setBaseFlows({ ...simplified, features: simplified.features.filter((feature) => feature.geometry?.type === 'LineString') });
        setEdgeMeta(edgeMetaFromGeoJSON(simplified));
      } else {
        loadMessageApiRef.current.error('Impossibile caricare la rete base');
      }

      if (networkResult.status === 'fulfilled') {
        const net = networkResult.value;
        setEdgeMeta((current) => {
          const merged = { ...current };
          Object.entries(net.edges || {}).forEach(([uid, catalogMeta]) => {
            const geometryMeta = current[uid] || {};
            merged[uid] = {
              ...geometryMeta,
              ...catalogMeta,
              // Geometry matching distinguishes parallel reverse edges better
              // than a first-match catalog fallback.
              opposite_uid: geometryMeta.opposite_uid || catalogMeta.opposite_uid || null,
              capacity_estimated: catalogMeta.capacity_is_observed !== true,
            };
          });
          return merged;
        });
      }
      const localScenarios = listLocalScenarios(clientType);
      const remoteScenarios = scenariosResult.status === 'fulfilled'
        ? (scenariosResult.value.scenarios || [])
        : [];
      setSavedScenarios([
        ...localScenarios,
        ...remoteScenarios.filter((remote) => !localScenarios.some((local) => String(local.id) === String(remote.id))),
      ]);

      // A new road belongs to the scenario that created it. Reopening the
      // module always starts from the unmodified baseline.
      setNewRoads([]);
      setInterventions({});
      setResultFlowsA(null);
      setLoadingNet(false);
    };
    loadBase();
  }, []);

  useSyncedMaps(mapARef, mapBRef, compareMode);

  const streetsByName = useMemo(() => {
    const map = new Map();
    if (!liveFlows?.features) return map;
    for (const f of liveFlows.features) {
      let name = getFirstName(f.properties?.name);
      if (!name || name === 'Strada') {
        name = 'Strada senza nome';
      }
      const uid = f.properties?.edge_uid;
      if (uid && name) {
        const key = name.trim();
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(f);
      }
    }
    return map;
  }, [liveFlows]);

  const focusedUids = useMemo(() => new Set(
    (focusedStreetName ? streetsByName.get(focusedStreetName) : [])
      ?.map((feature) => feature.properties?.edge_uid)
      .filter(Boolean) || [],
  ), [focusedStreetName, streetsByName]);

  const handleSelectSearchResult = (name) => {
    setFocusedStreetName(name || null);
    if (!name) return;
    const features = streetsByName.get(name);
    if (!features?.length) return;
    const targetMap = compareMode ? mapBRef.current : mapARef.current;
    const bounds = L.geoJSON(features).getBounds();
    if (bounds.isValid()) withLiveMap(targetMap, (map) => map.fitBounds(bounds, { padding: [100, 100], maxZoom: 19, animate: false }));
  };

  const handleOpenStreetFromSearch = (name) => {
    const feature = streetsByName.get(name)?.[0];
    if (!feature) return;
    handleSelectSearchResult(name);
    handleOpenLink(feature.properties?.edge_uid, feature);
  };

  const handleOpenLink = (uid, feature, latlng) => {
    const meta = inferMeta(uid, edgeMeta);
    const own = { ...emptyIntervention(), ...(interventions[uid] || {}) };
    const state = directionalClosureState(interventions, uid, {
      ...edgeMeta,
      [uid]: meta,
    });
    const forwardClosed = state.forward;
    const backwardClosed = state.backward;
    const synchronized = forwardClosed && backwardClosed
      ? { ...own, forward: false, backward: false, both: true }
      : { ...own, forward: forwardClosed, backward: backwardClosed, both: false };
    setLinkSel({ uid, feature, latlng, meta });
    setDraft(synchronized);
  };

  const directionUids = useMemo(() => {
    if (!linkSel) return hoveredUids;
    if (isOneWayEdge(linkSel.meta, linkSel.feature?.properties)) return [linkSel.uid];
    return [linkSel.uid, linkSel.meta?.opposite_uid].filter(Boolean);
  }, [linkSel, hoveredUids]);

  const selectionUids = useMemo(() => {
    const uids = new Set(focusedUids);
    if (linkSel) [linkSel.uid, linkSel.meta?.opposite_uid].filter(Boolean).forEach((uid) => uids.add(uid));
    return uids;
  }, [focusedUids, linkSel]);

  const previewClosedEdges = useMemo(() => {
    const next = new Set(closedEdgesA);
    if (!linkSel || !draft) return next;
    const mainClosed = !!(draft.forward || draft.both);
    const oppositeClosed = !!(draft.backward || draft.both);
    if (mainClosed) next.add(linkSel.uid);
    else next.delete(linkSel.uid);
    if (linkSel.meta?.opposite_uid) {
      if (oppositeClosed) next.add(linkSel.meta.opposite_uid);
      else next.delete(linkSel.meta.opposite_uid);
    }
    return next;
  }, [closedEdgesA, linkSel, draft]);

  const applyLink = () => {
    if (!linkSel || !draft) return;
    const uid = linkSel.uid;
    setInterventions((prev) => applyDirectionalClosure(
      prev,
      uid,
      { ...edgeMeta, [uid]: linkSel.meta },
      draft,
    ));
    setLinkSel(null);
    messageApi.success('Modifica applicata allo scenario corrente');
  };

  const mods = countInterventions(interventions, newRoads, edgeMeta);
  const currentDefinition = useMemo(
    () => toApiScenario(interventions, newRoads, edgeMeta),
    [interventions, newRoads, edgeMeta],
  );
  const currentFingerprint = useMemo(
    () => scenarioDefinitionFingerprint(currentDefinition),
    [currentDefinition],
  );
  useEffect(() => {
    setResultFlowsA(null);
    setNetworkKpis(null);
    setKpiDelta(null);
  }, [currentFingerprint]);
  const activeScenario = savedScenarios.find(
    (scenario) => String(scenario.id) === String(activeScenarioId),
  );
  const isScenarioDirty = mods > 0 && currentFingerprint !== savedScenarioFingerprint;
  const currentScenarioName = activeScenario?.name || `Scenario ${savedScenarios.length + 1}`;

  const executeSimulation = async () => {
    if (!startAction('Elaborazione simulazione AssignX…', { deferToRequest: true })) return;
    setRunningA(true);
    const clientType = getClientType();
    const scenario = currentDefinition;
    const applyLocalPreview = () => {
      const side = { interventions, newRoads };
      const localGeoJson = buildLocalScenarioGeoJson(baseFlows, graphNodes, side, edgeMeta);
      const analysis = computeScenarioKPIs(baseFlows?.features || [], interventions, newRoads, null, edgeMeta);
      setResultFlowsA(localGeoJson);
      setNetworkKpis(analysis.scenario);
      setKpiDelta(analysis.delta);
      messageApi.warning('API AssignX non disponibile: anteprima dello scenario calcolata localmente');
    };
    try {
      if (apiCapabilitiesRef.current.execution === false) {
        applyLocalPreview();
        return;
      }
      const data = await assignxApi.runAssignxV2(clientType, scenario);
      apiCapabilitiesRef.current.execution = true;
      if (!data.ok) throw new Error((data.validation?.errors || []).map((e) => e.message).join(', ') || 'Validazione fallita');
      const geo = mergeScenarioResult(
        withScenarioRoads(baseFlows, graphNodes, newRoads),
        data.scenario?.geojson,
        data.delta?.edge_deltas,
      );
      const analysis = computeScenarioKPIs(
        baseFlows?.features || [],
        interventions,
        newRoads,
        geo?.features,
        edgeMeta,
      );
      setResultFlowsA(geo);
      setNetworkKpis(data.scenario?.network_kpis || analysis.scenario);
      setKpiDelta(data.delta?.network_kpi_delta || analysis.delta);
      (data.validation?.warnings || []).forEach((w) => messageApi.warning(w.message));
      messageApi.success('Assegnazione aggiornata');
    } catch (e) {
      if (isEndpointUnavailable(e)) {
        apiCapabilitiesRef.current.execution = false;
        applyLocalPreview();
        return;
      }
      messageApi.error(`Esecuzione scenario fallita: ${assignxErrorMessage(e, 'errore sconosciuto')}`);
    } finally {
      setRunningA(false);
      finishAction();
    }
  };

  const runSimulation = () => {
    if (mods > 0 && isScenarioDirty) {
      setScName(currentScenarioName);
      setSavePromptPurpose('run');
      return;
    }
    executeSimulation();
  };

  const getComparisonSide = useCallback((selection) => {
    if (selection === 'base') {
      return {
        id: 'base',
        name: 'Scenario base',
        definition: { __assignx_base__: true, interventions: {}, new_roads: [] },
        interventions: {},
        newRoads: [],
      };
    }
    if (selection === 'current') {
      return {
        id: 'current',
        name: 'Scenario corrente',
        definition: toApiScenario(interventions, newRoads, edgeMeta),
        interventions,
        newRoads,
      };
    }
    const saved = savedScenarios.find((scenario) => String(scenario.id) === String(selection));
    if (!saved) return null;
    return {
      id: String(saved.id),
      name: saved.name,
      definition: saved.definition || {},
      interventions: fromApiInterventions(saved.definition),
      newRoads: (saved.definition?.new_roads || []).map(normalizePersistedRoad),
    };
  }, [edgeMeta, interventions, newRoads, savedScenarios]);

  const runComparison = useCallback(async (leftSelection, rightSelection) => {
    if (!startAction('Confronto scenari in corso…', { deferToRequest: true })) return false;
    const requestId = ++comparisonRequestRef.current;
    const left = getComparisonSide(leftSelection);
    const right = getComparisonSide(rightSelection);
    if (!left || !right) {
      messageApi.error('Scenario selezionato non disponibile');
      finishAction();
      return false;
    }
    const applyLocalComparison = () => {
      if (requestId !== comparisonRequestRef.current) return false;
      const leftAnalysis = computeScenarioKPIs(baseFlows?.features || [], left.interventions, left.newRoads, null, edgeMeta);
      const rightAnalysis = computeScenarioKPIs(baseFlows?.features || [], right.interventions, right.newRoads, null, edgeMeta);
      setComparison({
        left: {
          ...left,
          geojson: buildLocalScenarioGeoJson(baseFlows, graphNodes, left, edgeMeta),
          closedEdges: closedEdgeUids(left.interventions, edgeMeta),
        },
        right: {
          ...right,
          geojson: buildLocalScenarioGeoJson(baseFlows, graphNodes, right, edgeMeta),
          closedEdges: closedEdgeUids(right.interventions, edgeMeta),
        },
      });
      setKpiDelta(Object.fromEntries(
        Object.keys(rightAnalysis.scenario).map((key) => [
          key,
          Number(rightAnalysis.scenario[key] || 0) - Number(leftAnalysis.scenario[key] || 0),
        ]),
      ));
      messageApi.warning('API AssignX non disponibile: confronto calcolato localmente');
      return true;
    };
    setCompareRunning(true);
    try {
      if (apiCapabilitiesRef.current.execution === false) {
        return applyLocalComparison();
      }
      const result = await assignxApi.compareAssignxV2(
        getClientType(),
        left.definition,
        right.definition,
      );
      apiCapabilitiesRef.current.execution = true;
      if (!result.ok) throw result;
      if (requestId !== comparisonRequestRef.current) return false;
      setComparison({
        left: {
          ...left,
          geojson: mergeScenarioResult(
            withScenarioRoads(baseFlows, graphNodes, left.newRoads),
            result.left?.scenario?.geojson,
            result.left?.delta?.edge_deltas,
          ),
          closedEdges: closedEdgeUids(left.interventions, edgeMeta),
        },
        right: {
          ...right,
          geojson: mergeScenarioResult(
            withScenarioRoads(baseFlows, graphNodes, right.newRoads),
            result.right?.scenario?.geojson,
            result.right?.delta?.edge_deltas,
          ),
          closedEdges: closedEdgeUids(right.interventions, edgeMeta),
        },
      });
      setKpiDelta(result.delta?.network_kpi_delta || null);
      return true;
    } catch (error) {
      if (isEndpointUnavailable(error)) {
        apiCapabilitiesRef.current.execution = false;
        return applyLocalComparison();
      }
      messageApi.error(`Confronto fallito: ${assignxErrorMessage(error, 'Confronto non disponibile')}`);
      return false;
    } finally {
      setCompareRunning(false);
      finishAction();
    }
  }, [baseFlows, edgeMeta, finishAction, getComparisonSide, graphNodes, messageApi, startAction]);

  const handleToggleCompare = async () => {
    if (compareMode) {
      setCompareMode(false);
      setLinkSel(null);
      return;
    }
    if (savedScenarios.length < 2) {
      messageApi.warning('Crea almeno due scenari per confrontarli');
      return;
    }
    const left = String(savedScenarios[0].id);
    const right = String(savedScenarios[1].id);
    if (await runComparison(left, right)) {
      setCompareLeft(left);
      setCompareRight(right);
      setCompareMode(true);
      setNewRoadMode(false);
      setShowNewRoadForm(false);
      setLinkSel(null);
    }
  };

  const handleCompareLeft = async (selection) => {
    if (await runComparison(selection, compareRight)) {
      setCompareLeft(selection);
    }
  };

  const handleCompareRight = async (selection) => {
    if (await runComparison(compareLeft, selection)) {
      setCompareRight(selection);
    }
  };

  const handleSaveScenario = async (requestedName = scName) => {
    const name = String(requestedName || '').trim();
    if (!name) { messageApi.warning('Inserisci un nome scenario'); return null; }
    if (!startAction('Salvataggio scenario in corso…')) return null;
    try {
      const clientType = getClientType();
      const definition = currentDefinition;
      let response;
      try {
        if (activeScenario?.local) {
          response = updateLocalScenario(clientType, activeScenario.id, name, definition, networkKpis);
        } else if (apiCapabilitiesRef.current.scenarioWrites === false) {
          response = saveLocalScenario(clientType, name, definition, networkKpis);
        } else if (activeScenarioId) {
          response = await assignxApi.updateScenario(activeScenarioId, name, definition, networkKpis);
          apiCapabilitiesRef.current.scenarioWrites = true;
        } else {
          response = await assignxApi.saveScenario(clientType, name, definition, networkKpis);
          apiCapabilitiesRef.current.scenarioWrites = true;
        }
      } catch (error) {
        if (!canUseLocalFallback(error)) throw error;
        if (isEndpointUnavailable(error)) apiCapabilitiesRef.current.scenarioWrites = false;
        response = saveLocalScenario(clientType, name, definition, networkKpis);
      }
      const row = response.scenario || response;
      if (row.id == null || !row.definition) {
        throw new Error('Il server non ha restituito lo scenario salvato');
      }
      setSavedScenarios((prev) => [
        row,
        ...prev.filter((scenario) => (
          String(scenario.id) !== String(row.id)
          && (!activeScenarioId || String(scenario.id) !== String(activeScenarioId))
        )),
      ]);
      setActiveScenarioId(row.id);
      setSavedScenarioFingerprint(currentFingerprint);
      setScName('');
      messageApi.success(row.local ? 'Scenario salvato localmente' : 'Scenario salvato');
      return row;
    } catch (e) {
      messageApi.error(assignxErrorMessage(e, 'Impossibile salvare lo scenario'));
      return null;
    } finally {
      finishAction();
    }
  };

  const handleSavePromptOk = async () => {
    const purpose = savePromptPurpose;
    const name = scName;
    // Close before saving so only the loading screen remains in the foreground.
    setSavePromptPurpose(null);
    const saved = await handleSaveScenario(name);
    if (!saved) {
      setScName(name);
      setSavePromptPurpose(purpose);
      return;
    }
    if (purpose === 'run') await executeSimulation();
  };

  const handleRunWithoutSaving = async () => {
    setSavePromptPurpose(null);
    await executeSimulation();
  };

  const handleLoadScenario = (item) => {
    const def = item.definition || {};
    const mapped = fromApiInterventions(def);
    const loadedRoads = (def.new_roads || []).map(normalizePersistedRoad);
    setInterventions(mapped);
    setNewRoads(loadedRoads);
    setActiveScenarioId(item.id);
    setSavedScenarioFingerprint(scenarioDefinitionFingerprint(toApiScenario(mapped, loadedRoads, edgeMeta)));
    setResultFlowsA(null);
    setNetworkKpis(null);
    setKpiDelta(null);
    setComparison(null);
    setCompareMode(false);
    setDrawer(null);
    messageApi.success(`Caricato: ${item.name}`);
  };

  const pickNode = useCallback((c) => {
    setNewRoadDraft((prev) => {
      if (!prev.origin) return { ...prev, origin: c.id };
      if (!prev.destination && prev.origin !== c.id) {
        setShowNewRoadForm(true);
        return { ...prev, destination: c.id };
      }
      return prev;
    });
  }, []);

  const confirmNewRoad = () => {
    const errs = validateNewRoad(newRoadDraft.origin, newRoadDraft.destination, newRoadDraft.lanes, newRoadDraft.speed);
    if (errs.length) { messageApi.error(errs[0]); return; }
    const cap = Math.max(1, Math.round((newRoadDraft.capacity || estimateCapacityVph(newRoadDraft.highway, newRoadDraft.lanes)) * (newRoadDraft.factor || 1)));
    const origin = graphNodes.find((node) => node.id === newRoadDraft.origin);
    const destination = graphNodes.find((node) => node.id === newRoadDraft.destination);
    if (!origin || !destination) { messageApi.error('Nodi selezionati non disponibili'); return; }
    const road = normalizePersistedRoad({
      ...newRoadDraft,
      uid: `nr_${Date.now()}`,
      graph_u: origin.id,
      graph_v: destination.id,
      capacity: cap,
      geometry: { type: 'LineString', coordinates: [[origin.lon, origin.lat], [destination.lon, destination.lat]] },
      name: newRoadDraft.name.trim() || `${origin.name} – ${destination.name}`,
    });
    setNewRoads((prev) => [road, ...prev]);
    setNewRoadMode(false);
    setShowNewRoadForm(false);
    setNewRoadDraft({ origin: null, destination: null, highway: 'residential', lanes: 1, speed: 30, capacity: 400, capacityAuto: true, factor: 1, twoWay: false, name: '' });
    messageApi.success('Nuova strada aggiunta allo scenario');
  };

  const compareOptions = savedScenarios.map((scenario) => ({ id: String(scenario.id), name: scenario.name }));
  const canCompare = savedScenarios.length >= 2;

  return (
    <div className={`assignx-page ${compareMode ? 'compare-mode' : ''}`}>
      {ctx}
      <div className="assignx-top-controls" style={{ right: '20px', left: 'auto' }}>
        <AssignxSideCard
          baseFlows={liveFlows}
          closedEdges={closedEdgesA}
          setClosedEdges={updateClosedEdges}
          onSelectSearchResult={handleSelectSearchResult}
          onOpenStreet={handleOpenStreetFromSearch}
          streetsByName={streetsByName}
          setHoveredUids={setHoveredUids}
          edgeMeta={edgeMeta}
        />
        <AntTooltip title="Nuova strada" placement="bottom">
          <button aria-label="Nuova strada" className={`sidebar-button ${newRoadMode ? 'active' : ''}`} disabled={actionBusy || compareMode} onClick={() => { setNewRoadMode((v) => !v); setShowNewRoadForm(false); setNewRoadDraft((p) => ({ ...p, origin: null, destination: null })); }}><PlusOutlined /></button>
        </AntTooltip>
        <AntTooltip title="Interventi e Scenari" placement="bottom">
          <button aria-label="Interventi e Scenari" className={`sidebar-button ${drawer === 'int' ? 'active' : ''}`} disabled={actionBusy || (mods === 0 && savedScenarios.length === 0)} onClick={() => setDrawer(drawer === 'int' ? null : 'int')}><UnorderedListOutlined />{mods > 0 && <span className="assignx-int-badge">{mods}</span>}</button>
        </AntTooltip>
        <AntTooltip title="Avvia Simulazione" placement="bottom">
          <button aria-label="Avvia Simulazione" className={`sidebar-button ${runningA ? 'active' : ''}`} onClick={runSimulation} disabled={actionBusy || compareMode}><PlayCircleOutlined /></button>
        </AntTooltip>
        <AntTooltip title={compareMode ? 'Esci dal confronto' : (canCompare ? 'Compara scenari' : 'Crea almeno due scenari per confrontarli')} placement="bottom">
          <button aria-label={compareMode ? 'Esci dal Confronto' : 'Compara scenari'} className={`sidebar-button-confronto ${compareMode ? 'active' : ''}`} onClick={handleToggleCompare} disabled={actionBusy || compareRunning || (!compareMode && !canCompare)}>
            {compareMode ? <CloseOutlined className="confronto-icon" /> : <img src={ExchangeIcon} alt="" className="confronto-icon" />}
            <span>{compareMode ? 'Esci' : 'Confronto'}</span>
          </button>
        </AntTooltip>
      </div>

      {newRoadMode && <div className="assignx-select-banner">NUOVA STRADA — {!newRoadDraft.origin ? 'seleziona il nodo di ORIGINE' : !newRoadDraft.destination ? 'seleziona il nodo di DESTINAZIONE' : 'configura e conferma il collegamento'}</div>}

      {compareMode && kpiDelta && (
        <div className="assignx-compare-delta" aria-label="Differenza tra scenari">
          {[
            ['total_flow_on_edges', 'Flusso'],
            ['total_delay', 'Ritardo'],
            ['mean_speed', 'Velocità'],
          ].filter(([key]) => kpiDelta[key] != null).map(([key, label]) => {
            const value = Number(kpiDelta[key]);
            return (
              <span key={key} className={`assignx-delta-chip ${value > 0 ? 'up' : 'down'}`}>
                <span className="lbl">{label}</span>
                {value > 0 ? '+' : ''}{value.toFixed(1)}
              </span>
            );
          })}
        </div>
      )}

      <div className="assignx-split-container">
        <AssignxMapView
          id="mapA" mapRef={mapARef} mapTheme={mapTheme} baseFlows={compareMode ? baseFlows : liveFlows}
          resultFlows={compareMode ? comparison?.left?.geojson : resultFlowsA}
          closedEdges={compareMode ? (comparison?.left?.closedEdges || EMPTY_SET) : previewClosedEdges}
          interventions={compareMode ? (comparison?.left?.interventions || {}) : interventions}
          edgeMeta={edgeMeta}
          newRoads={compareMode ? (comparison?.left?.newRoads || []) : newRoads}
          onMapBackgroundClick={() => { setLinkSel(null); setFocusedStreetName(null); }}
          onSelectSearchResult={handleSelectSearchResult}
          streetsByName={streetsByName} externalLayerMapRef={layerMapARef}
          hoveredUids={directionUids} focusedUids={selectionUids} baseMax={baseMax}
          onOpenLink={handleOpenLink}
          newRoadMode={newRoadMode}
          graphNodes={graphNodes}
          newRoadDraft={newRoadDraft}
          onPickNode={pickNode}
          scenarioSelect={compareMode ? {
            label: 'Scenario sinistro',
            value: compareLeft,
            options: compareOptions,
            disabled: compareRunning,
            onChange: handleCompareLeft,
          } : null}
        />
        {compareMode && (
          <AssignxMapView
            id="mapB" mapRef={mapBRef} mapTheme={mapTheme} baseFlows={baseFlows}
            resultFlows={comparison?.right?.geojson}
            closedEdges={comparison?.right?.closedEdges || EMPTY_SET}
            interventions={comparison?.right?.interventions || {}}
            edgeMeta={edgeMeta}
            newRoads={comparison?.right?.newRoads || []}
            scenarioSelect={{
              label: 'Scenario destro',
              value: compareRight,
              options: compareOptions,
              disabled: compareRunning,
              onChange: handleCompareRight,
            }}
            onMapBackgroundClick={() => { setLinkSel(null); setFocusedStreetName(null); }}
            streetsByName={streetsByName} externalLayerMapRef={layerMapBRef}
            hoveredUids={directionUids} focusedUids={selectionUids} baseMax={baseMax}
            onOpenLink={handleOpenLink}
            graphNodes={graphNodes}
            autoFit={false}
          />
        )}
      </div>

      {linkSel && (
        <LinkDetailPanel
          feature={linkSel.feature}
          meta={linkSel.meta}
          draft={draft}
          setDraft={setDraft}
          onClose={() => setLinkSel(null)}
          onApply={applyLink}
        />
      )}

      <InterventionsDrawer
        open={drawer === 'int'}
        interventions={interventions}
        newRoads={newRoads}
        edgeMeta={edgeMeta}
        featuresByUid={featuresByUid}
        comparisonSides={compareMode && comparison ? [
          { key: 'left', position: 'Sinistra', ...comparison.left },
          { key: 'right', position: 'Destra', ...comparison.right },
        ] : null}
        onClose={() => setDrawer(null)}
        onReset={() => {
          setInterventions({});
          setNewRoads([]);
          setResultFlowsA(null);
          setNetworkKpis(null);
          setKpiDelta(null);
          setLinkSel(null);
          setActiveScenarioId(null);
          setSavedScenarioFingerprint(null);
          messageApi.success('Scenario azzerato');
        }}
        onSave={() => {
          setScName(currentScenarioName);
          setSavePromptPurpose('save');
        }}
        scenarios={savedScenarios}
        activeScenarioId={activeScenarioId}
        currentScenarioName={currentScenarioName}
        isDirty={isScenarioDirty}
        onLoad={handleLoadScenario}
        onDelete={async (item) => {
          if (!startAction('Eliminazione scenario in corso…')) return;
          try {
            deleteLocalScenario(getClientType(), item.id);
            if (!item.local && apiCapabilitiesRef.current.scenarios !== false) {
              await assignxApi.deleteScenario(item.id);
            }
            setSavedScenarios((current) => current.filter((scenario) => String(scenario.id) !== String(item.id)));
            if (String(activeScenarioId) === String(item.id)) {
              setActiveScenarioId(null);
              setSavedScenarioFingerprint(null);
            }
          } catch (error) {
            messageApi.error(assignxErrorMessage(error, 'Impossibile eliminare lo scenario'));
          } finally {
            finishAction();
          }
        }}
        onCompare={async (item) => {
          const right = String(item.id);
          setCompareLeft('base');
          setCompareRight(right);
          setDrawer(null);
          if (await runComparison('base', right)) setCompareMode(true);
        }}
        busy={actionBusy}
      />
      <Modal
        open={!!savePromptPurpose}
        title={savePromptPurpose === 'run' ? 'Salva lo scenario prima della simulazione' : 'Salva scenario'}
        okText={savePromptPurpose === 'run' ? 'Salva e simula' : 'Salva'}
        cancelText="Annulla"
        okButtonProps={{ disabled: !scName.trim() }}
        onCancel={() => !actionBusy && setSavePromptPurpose(null)}
        onOk={handleSavePromptOk}
        footer={savePromptPurpose === 'run' ? [
          <Button key="cancel" disabled={actionBusy} onClick={() => setSavePromptPurpose(null)}>
            Annulla
          </Button>,
          <Button key="run-without-save" disabled={actionBusy} onClick={handleRunWithoutSaving}>
            Simula senza salvare
          </Button>,
          <Button key="save-and-run" type="primary" disabled={actionBusy || !scName.trim()} onClick={handleSavePromptOk}>
            Salva e simula
          </Button>,
        ] : undefined}
      >
        <p>
          {savePromptPurpose === 'run'
            ? 'Puoi salvare lo scenario prima della simulazione oppure eseguirlo senza salvarlo.'
            : 'Inserisci un nome per non perdere le modifiche configurate.'}
        </p>
        <Input value={scName} onChange={(event) => setScName(event.target.value)} placeholder="Nome scenario" />
      </Modal>
      <NewRoadPanel open={showNewRoadForm} draft={newRoadDraft} setDraft={setNewRoadDraft} onCancel={() => { setShowNewRoadForm(false); setNewRoadMode(false); }} onConfirm={confirmNewRoad} busy={actionBusy} />

      <div className="assignx-theme-toggle">
        <ThemeToggleButton theme={mapTheme} onChange={setMapTheme} />
      </div>

      <ModuleSlider
        showNavbarControls={false} setShowNavbarControls={() => { }}
        showSimfixAreas={false} setShowSimfixAreas={() => { }}
        showPredictMode={false} setShowPredictMode={() => { }}
        showTlGreenWave={false} setShowTlGreenWave={() => { }}
        showAssignx={true} setShowAssignx={() => { }}
      />
    </div>
  );
}
